import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../services/apiClient';
import { notifyJourneyChanged } from '../services/companyJourneyService';

/**
 * The interviews this side has not reported on yet, and the way to answer.
 *
 * A failed read reports an empty list rather than throwing: the prompt is a
 * nudge, and an unreachable endpoint must not break the page it sits on.
 */
export default function useInterviewFeedback() {
  const [pending, setPending] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const rows = await apiClient.get('/interview-feedback/pending');
      if (!mounted.current) return;
      setPending(Array.isArray(rows) ? rows : []);
    } catch {
      if (!mounted.current) return;
      setPending([]);
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Re-arm in SETUP, not only in cleanup. React StrictMode double-invokes
    // mount effects in dev: the first pass's cleanup sets this false, and a
    // flag that is only ever set false leaves every later response discarded.
    mounted.current = true;
    refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const submit = useCallback(
    async (payload) => {
      const result = await apiClient.post('/interview-feedback', payload);
      // An interview completes once both sides have answered and the
      // PROVIDER's answer is 'happened' (theirs settles a disagreement), which
      // is a marquee milestone and one half of the Premium verdict. Announced before
      // the local refresh so the board and the prompt settle together, and only
      // after the post resolved: an answer that did not save moved nothing.
      // Guarded so a broken listener cannot turn a recorded answer into a
      // rejected submit.
      try {
        notifyJourneyChanged();
      } catch {
        /* the journey board is decoration; it must never fail the answer it followed */
      }
      await refresh();
      return result;
    },
    [refresh],
  );

  return { pending, submit, isLoading, refresh };
}
